import { createContext, useContext } from 'react';

import type {
  StorefrontCardView,
  WeddingCardView,
  SeoContentView,
  SearchLocationView,
  USCityView,
  SearchLocationReviewListView,
  SeoSrpMetricDataView,
} from '@zola/svc-marketplace-ts-types';

import { UserSettingsObjectType } from '~/actions/couples/types/userSettingsActionTypes';
import type { VendorCardVendor } from '~/components/common/cards/util/types';
import type {
  AccountVendorPreferencesView,
  AccountVendorView,
  AccountWeddingPreferencesView,
  SaveAccountWeddingPreferencesRequest,
  TaxonomyWithFacetsView,
  UpdateAccountVendorPreferenceRequest,
} from '~/types/responseTypes';
import { OptionFacetCategoryType } from '~/types/types';
import { FacetsByVendorType } from '~/util/facetUtils';

import type { ParseVendorSearchUrlResult } from '../../../util/parseVendorSearchUrl';
import type { PageCardView } from '../../ExpertAdvice/ExpertAdvice';
import type { VendorSearchRequest } from '../types/types';

/**
 * Mapped result from an API search response
 */
export type SearchResultPageSearchResult = {
  /**
   * Total number of results when the search was executed (may be an approximate number)
   */
  totalHits: number;

  /**
   * Current offset in the total set of search results
   */
  offset: number;

  /**
   * Storefront search results
   */
  vendors: StorefrontCardView[];

  /**
   * Whether the mini quiz should be inserted on the first page
   */
  shouldInsertMiniQuiz: boolean;

  /**
   * Whether the budget quiz should be inserted on the first page
   */
  shouldInsertBudgetQuiz: boolean;
};

/**
 * Props common to both pre and post-auth search results pages
 */
type SearchResultsPageCommonProps = {
  parsedUrl: ParseVendorSearchUrlResult;

  taxonomiesWithFacets: TaxonomyWithFacetsView[];

  facetsByVendorType: FacetsByVendorType;

  /**
   * Search filters for the category
   */
  searchFilters: OptionFacetCategoryType[];

  /**
   * Current search results (vendors, total hits, etc.)
   */
  searchResults: SearchResultPageSearchResult;

  /**
   * Vendors in adjacent markets to show for SEO purposes
   */
  adjacentVendors: StorefrontCardView[];

  /**
   * EA articles to show for SEO purposes
   */
  expertAdvice: PageCardView[];

  /**
   * Real weddings to show for SEO purposes
   */
  realWeddings: WeddingCardView[];

  /**
   * We hold out a certain percentage of users from seeing featured vendors
   */
  isInHoldoutGroupExperiment: boolean;

  /**
   * Vendors should never be held out from seeing featured vendors
   */
  identifiedAsVendor: boolean;

  /**
   * Are we loading new data after a search change? Used to provide user feedback (e.g. to blur page elements)
   */
  loading: boolean;
};

export const DEFAULT_PARSE_VENDOR_SEARCH_URL_RESULT: ParseVendorSearchUrlResult = {
  canonicalUrl: '',
  seoCanonicalUrl: '',
  vendorSearch: {
    page: 1,
  },
  selectedFacetOptions: [],
};

const DEFAULT_SEARCH_RESULTS_PAGE_COMMON_PROPS: SearchResultsPageCommonProps = {
  parsedUrl: DEFAULT_PARSE_VENDOR_SEARCH_URL_RESULT,
  taxonomiesWithFacets: [],
  facetsByVendorType: {},
  searchFilters: [],
  searchResults: {
    totalHits: 0,
    offset: 0,
    vendors: [],
    shouldInsertMiniQuiz: false,
    shouldInsertBudgetQuiz: false,
  },
  adjacentVendors: [],
  expertAdvice: [],
  realWeddings: [],
  isInHoldoutGroupExperiment: false,
  identifiedAsVendor: false,
  loading: false,
};

/**
 * Props specific to pre-auth search results pages
 */
type PreAuthSearchResultsPageProps = {
  /**
   * Highlighted reviews for the SRP
   */
  searchLocationReviews: SearchLocationReviewListView | null;

  /**
   * SEO metrics to determine whether we should index the page
   */
  seoMetrics: SeoSrpMetricDataView | null;

  /**
   * SEO content generated by the back-end
   */
  seoContent: SeoContentView | null;

  /**
   * List of top locations for generating SEO cross links
   */
  seoTopLocations: SearchLocationView[];

  /**
   * List of nearby cities for generating SEO cross links
   */
  seoNearbyCities: USCityView[];

  /**
   * Whether the page type is `near-me` vs. `srp`
   */
  isNearMe: boolean;

  /**
   * Release ramp for pros and cons on the pre-auth vendor card
   */
  preAuthSrpProsAndCons: boolean;
};

const DEFAULT_PRE_AUTH_SEARCH_RESULTS_PAGE_PROPS: PreAuthSearchResultsPageProps = {
  searchLocationReviews: null,
  seoMetrics: null,
  seoContent: null,
  seoTopLocations: [],
  seoNearbyCities: [],
  isNearMe: false,
  preAuthSrpProsAndCons: false,
};

/**
 * Props specific to post-auth search results pages
 */
type PostAuthSearchResultsPageProps = {
  /**
   * Whether we show top picks
   */
  showTopPicks: boolean;

  /**
   * List of top picks to show
   */
  topPicks: StorefrontCardView[];

  /**
   * List of recently viewed vendors
   */
  recentlyViewed: VendorCardVendor[];

  /**
   * User settings to load into Redux
   */
  userSettings: UserSettingsObjectType;

  /**
   * A user's account vendor records (booked vendors) to load into Redux
   */
  accountVendors: AccountVendorView[];

  /**
   * Wedding preferences for a user (e.g. wedding location)
   */
  weddingPreferences: AccountWeddingPreferencesView | null;

  /**
   * Vendor preferences for a user (e.g. budget)
   */
  vendorPreferences: AccountVendorPreferencesView[];

  /**
   * Storefronts the user has inquired with
   */
  storefrontToInquiryMap: Record<string, string>;

  isSRPLinkExperiment?: boolean;
};

const DEFAULT_POST_AUTH_SEARCH_RESULTS_PAGE_PROPS: PostAuthSearchResultsPageProps = {
  showTopPicks: false,
  topPicks: [],
  recentlyViewed: [],
  userSettings: {},
  accountVendors: [],
  weddingPreferences: null,
  vendorPreferences: [],
  storefrontToInquiryMap: {},
  isSRPLinkExperiment: false,
};

/**
 * Mutators provided by the context to update the page state
 */
export type SearchResultsPageMutators = {
  setVendorSearch: (vendorSearch: VendorSearchRequest) => void;

  updateWeddingPreferences: (
    weddingPreferencesUuid: string | null,
    values: SaveAccountWeddingPreferencesRequest
  ) => void;

  updateVendorPreferences: (
    vendorPreferencesUuid: string | null,
    values: UpdateAccountVendorPreferenceRequest
  ) => void;
};

const DEFAULT_SEARCH_RESULTS_PAGE_MUTATORS: SearchResultsPageMutators = {
  setVendorSearch: () => undefined,
  updateWeddingPreferences: () => undefined,
  updateVendorPreferences: () => undefined,
};

export type SearchResultsPageContextValues = ParseVendorSearchUrlResult &
  SearchResultsPageCommonProps &
  PreAuthSearchResultsPageProps &
  PostAuthSearchResultsPageProps &
  SearchResultsPageMutators;

export const DEFAULT_SEARCH_RESULTS_CONTEXT_VALUES: SearchResultsPageContextValues = {
  ...DEFAULT_PARSE_VENDOR_SEARCH_URL_RESULT,
  ...DEFAULT_SEARCH_RESULTS_PAGE_COMMON_PROPS,
  ...DEFAULT_PRE_AUTH_SEARCH_RESULTS_PAGE_PROPS,
  ...DEFAULT_POST_AUTH_SEARCH_RESULTS_PAGE_PROPS,
  ...DEFAULT_SEARCH_RESULTS_PAGE_MUTATORS,
};

export type SearchResultsPageProviderProps = SearchResultsPageCommonProps &
  PreAuthSearchResultsPageProps &
  PostAuthSearchResultsPageProps;

export type PreAuthSearchResultsPageProviderProps = SearchResultsPageCommonProps &
  PreAuthSearchResultsPageProps;

export type PostAuthSearchResultsPageProviderProps = SearchResultsPageCommonProps &
  PostAuthSearchResultsPageProps;

export const SearchResultsPageContext = createContext<SearchResultsPageContextValues | undefined>(
  undefined
);

export const useSearchResultsPageContext = () => {
  const context = useContext(SearchResultsPageContext);
  if (context === undefined) {
    throw new Error('useSearchResultsPageContext must be used within a SearchResultsPageContext');
  }
  return context;
};
